import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { inbuiltScrapingApi } from '../lib/api';
import { routeMissing, serverError, legacyUrl, gridLines, issueCounts, inbuiltSaveBody, rowProgress } from '../lib/v3Backlog';
import PageHeader from '../components/PageHeader';
import usePageTitle from '../hooks/usePageTitle';
import { TableScroll } from '../components/ResponsiveTable';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { ScanSearch, Save, Loader2, AlertTriangle, Info, Lock, Trash2, RefreshCw, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';

/*
 * Growth > Inbuilt scraping - the inbuilt-scraping block of the legacy /orderManagement/accountSettings, as its own page
 * (r16o3 v3-backlog, future todo 2). V1 shows it to every account (outside the page's SMM and private-dashboard gates), and
 * so does this.
 *
 * What it is, from the server's cron (cronInbuiltScraping, every 10 s while the switch is on): each line of the grid becomes
 * an Active scraping; the server scrapes usernames for its target, a chunk per run, and adds them to the target list of the
 * slots the line names; a finished one moves to the history (last 200). Every scraper request costs one target-scraping
 * credit (R16I), which only the provider tops up - so the credits are shown, never edited.
 *
 * Two things shape the page:
 *   1. The grid is a QUEUE, not a setting. The server empties it once it has taken the lines. So the page sends the grid only
 *      when it was edited, together with the value it loaded; if the server took those lines meanwhile it refuses (409) and
 *      the page reloads, instead of scraping them twice. While nothing is being edited the page refreshes every 15 s.
 *   2. A line the server cannot read used to vanish with every other new line of that save. The server now refuses such a
 *      grid (422, nothing saved) and names the line; the page shows that list and keeps what was typed.
 *
 * Server: r16o3 GET / PUT /uiapi/inbuilt-scraping, DELETE /uiapi/inbuilt-scraping/active?id=. A server without them gets a
 * banner pointing to the legacy page. Switch: lib/r14Switch r16o3UiEnabled (App.js route).
 */

const FIELD_LABEL = {
  type: 'type', target: 'target', slots: 'slots', speed: 'speed', assign_low_threshold: 'assign low threshold',
  assign_chunk_size: 'assign chunk size', amount: 'amount', direct_assign: 'direct assign', refresh_list: 'refresh list',
};
/** Edited since it was loaded: the switch or the grid differs from what the server sent (lib/v3Backlog inbuiltSaveBody). */
function isDirty(f) {
  return !!(f && f.loaded && Object.keys(inbuiltSaveBody(f.loaded, f)).length > 0);
}

const EXAMPLE = 'AccountFollowers <> cat_sunleashed <> slot1,slot2 <> 1000 <> 100 <> 500 <> 2500 <> false <> false';

/*
 * A row with a BLANK id has not started (the server gives a row its id when it starts it). The r16o3 server says why
 * (waiting / waiting_reason, InbuiltScrapingGridCheck.waiting; review should-fix 2026-10-24: every such row read "starting").
 * Rows are deleted by id, so such a row has no Delete, here as on the legacy page. An older server sends no reason.
 */
const WAITING_WORDS = {
  service_off: 'waiting · service off',
  no_target_credits: 'waiting · 0 credits',
  blank_target: 'never starts · blank target',
  unreadable: 'unreadable row',
  starting: 'starting…',
};
const WAITING_TONE = { no_target_credits: 'text-warning', blank_target: 'text-danger', unreadable: 'text-danger' };

function WaitingLabel({ row }) {
  return (
    <span className={cn(WAITING_TONE[row.waiting] || 'text-muted-foreground')} title={row.waiting_reason || 'the server has not started this row yet'}
      data-testid="scrape-waiting" data-waiting={row.waiting || ''}>
      {WAITING_WORDS[row.waiting] || 'starting…'}
    </span>
  );
}

/** One line per reason under the Active table: why the rows without an id wait, and why they cannot be deleted yet. */
function NotStartedNote({ rows }) {
  const waiting = rows.filter((r) => !r.id);
  if (waiting.length === 0) return null;
  const reasons = [...new Set(waiting.map((r) => r.waiting_reason).filter(Boolean))];
  return (
    <div className="mt-3 flex items-start gap-2 rounded-lg bg-secondary/40 px-3 py-2.5 text-xs text-muted-foreground" data-testid="scrape-not-started">
      <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <div className="space-y-1">
        <p>
          {waiting.length} row{waiting.length === 1 ? ' has' : 's have'} not started. A row gets its id when the server starts it,
          and rows are deleted by their id, so {waiting.length === 1 ? 'it' : 'they'} cannot be deleted until then (the legacy page
          cannot either).
        </p>
        {reasons.map((t) => <p key={t}>{t}</p>)}
      </div>
    </div>
  );
}

function IssueList({ issues }) {
  if (!issues || issues.length === 0) return null;
  const sorted = [...issues].sort((a, b) => (a.severity === b.severity ? a.line - b.line : a.severity === 'error' ? -1 : 1));
  return (
    <ul className="mt-2 space-y-1.5" data-testid="scrape-grid-issues">
      {sorted.map((i, n) => (
        <li key={n} className={cn('flex items-start gap-2 rounded-lg px-2.5 py-1.5 text-xs',
          i.severity === 'error' ? 'bg-danger/10 text-danger' : 'bg-warning/10 text-warning')}>
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{i.message}</span>
        </li>
      ))}
    </ul>
  );
}

function Progress({ row }) {
  const p = rowProgress(row);
  if (!p) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <div className="min-w-[120px]">
      <span className="tnum text-sm text-foreground">{p.scraped.toLocaleString()}<span className="text-muted-foreground"> / {p.amount.toLocaleString()}</span></span>
      <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-muted" aria-hidden>
        <div className={cn('h-full rounded-full', p.pct >= 100 ? 'bg-success' : 'bg-primary')} style={{ width: `${p.pct}%` }} />
      </div>
      <p className="mt-0.5 text-[11px] text-muted-foreground tnum">{p.assigned.toLocaleString()} added to slots</p>
    </div>
  );
}

function DeleteButton({ id, pending, onDelete }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="ghost" size="icon" disabled={pending} title={`Delete ${id} scrapings`} aria-label={`Delete ${id} scrapings`}
          className="text-muted-foreground hover:text-danger hover:bg-danger/10" data-testid="scrape-delete">
          {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete the {id} scrapings?</AlertDialogTitle>
          <AlertDialogDescription>
            This stops and removes every active scraping with the id {id}, and the file of usernames scraped for it. Usernames
            already added to slot target lists stay there. It cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onDelete} className="bg-danger text-danger-foreground hover:bg-danger/90" data-testid="scrape-delete-confirm">
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function RowsTable({ rows, kind, pendingId, onDelete, testId }) {
  if (rows.length === 0) {
    return <p className="text-sm text-muted-foreground" data-testid={`${testId}-empty`}>{kind === 'active' ? 'No active scrapings.' : 'No completed scrapings yet.'}</p>;
  }
  const deletable = (r) => kind === 'active' && r.id;
  const mobile = rows.map((r, n) => (
    <div key={`${r.id}-${n}`} className="surface p-3" data-testid={`${testId}-card`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate font-medium text-foreground">{r.id || <WaitingLabel row={r} />}</p>
          <p className="truncate text-xs text-muted-foreground">{r.type || r.raw}{r.slots ? ` · ${r.slots}` : ''}</p>
        </div>
        {deletable(r) && <DeleteButton id={r.id} pending={pendingId === r.id} onDelete={() => onDelete(r.id)} />}
      </div>
      <div className="mt-2 flex items-center justify-between gap-3">
        <Progress row={r} />
        {r.status && <Badge variant="outline" className="text-muted-foreground">{r.status}</Badge>}
      </div>
    </div>
  ));
  return (
    <TableScroll mobile={mobile} minWidth="860px">
      <Table>
        <TableHeader>
          <TableRow className="border-border hover:bg-transparent">
            <TableHead>Id</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Target</TableHead>
            <TableHead>Slots</TableHead>
            <TableHead>Scraped</TableHead>
            <TableHead className="text-right">Speed / chunk</TableHead>
            <TableHead>Status</TableHead>
            {kind === 'active' && <TableHead className="w-10"><span className="sr-only">Delete</span></TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r, n) => (
            <TableRow key={`${r.id}-${n}`} className="border-border" data-testid={`${testId}-row`}>
              <TableCell className="max-w-[180px] truncate font-medium" title={r.id || undefined}>{r.id || <WaitingLabel row={r} />}</TableCell>
              {r.type !== undefined ? (
                <>
                  <TableCell className="text-sm">{r.type}{r.direct_assign === 'true' && <p className="text-[11px] text-muted-foreground">direct assign</p>}</TableCell>
                  <TableCell className="max-w-[160px] truncate text-sm text-muted-foreground" title={r.target}>{r.target || '—'}</TableCell>
                  <TableCell className="max-w-[180px] truncate text-sm" title={r.slots}>{r.slots}</TableCell>
                  <TableCell><Progress row={r} /></TableCell>
                  <TableCell className="text-right text-sm tnum text-muted-foreground">{r.speed} / {r.assign_chunk_size}</TableCell>
                  <TableCell><Badge variant="outline" className={cn(r.status === 'active' ? 'border-info/40 text-info' : 'text-muted-foreground')}>{r.status}</Badge></TableCell>
                </>
              ) : (
                <TableCell colSpan={6} className="font-mono text-xs text-muted-foreground break-all">{r.raw}</TableCell>
              )}
              {kind === 'active' && (
                <TableCell className="text-right">
                  {deletable(r) && <DeleteButton id={r.id} pending={pendingId === r.id} onDelete={() => onDelete(r.id)} />}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableScroll>
  );
}

export default function InbuiltScrapingPage() {
  usePageTitle('Inbuilt scraping');
  const queryClient = useQueryClient();
  const [form, setForm] = useState(null);
  // the issues of a refused save (422) - shown instead of the stored grid's until the next save or a fresh load
  const [saveIssues, setSaveIssues] = useState(null);
  const [pendingId, setPendingId] = useState(null);

  const dirty = isDirty(form);

  const q = useQuery({
    queryKey: ['inbuilt-scraping'],
    queryFn: () => inbuiltScrapingApi.get().then((r) => r.data),
    retry: (n, err) => !routeMissing(err) && n < 1,
    refetchInterval: dirty ? false : 15000,
  });

  // seed (and re-seed after each refresh) from the server, but never over what is being edited
  useEffect(() => {
    if (!q.data) return;
    setForm((f) => (isDirty(f) ? f
      : { enable: !!q.data.enable_inbuilt_scraping_service, grid: q.data.inbuilt_scraping_settings_grid || '', loaded: q.data }));
  }, [q.data]);

  const saveMut = useMutation({
    mutationFn: () => inbuiltScrapingApi.save(inbuiltSaveBody(form.loaded, form)),
    onSuccess: (res) => {
      // eslint-disable-next-line no-console
      console.info(`[R16O3-INBUILT] saved: ignored ${JSON.stringify(res.data?.ignored || [])}, ${(res.data?.grid_issues || []).length} warning(s)`);
      toast.success('Saved. New rows move to Active scrapings within about 10 seconds while the service is on.');
      setSaveIssues(null);
      setForm(null);
      queryClient.invalidateQueries({ queryKey: ['inbuilt-scraping'] });
    },
    onError: (err) => {
      const status = err?.response?.status;
      // eslint-disable-next-line no-console
      console.warn(`[R16O3-INBUILT] save refused: HTTP ${status || 'none'}`);
      if (status === 422) {
        setSaveIssues(err.response.data?.grid_issues || []);
        toast.error('Not saved: fix the errors in the grid.');
      } else if (status === 409) {
        toast.error(`Not saved: ${serverError(err)}`);
        setSaveIssues(null);
        setForm(null);
        queryClient.invalidateQueries({ queryKey: ['inbuilt-scraping'] });
      } else {
        toast.error(`Not saved: ${serverError(err)}`);
      }
    },
  });

  const delMut = useMutation({
    mutationFn: (id) => inbuiltScrapingApi.removeActive(id),
    onMutate: (id) => setPendingId(id),
    onSuccess: (res, id) => {
      toast.success(`Deleted ${res.data?.rows_removed || 1} ${id} scraping${(res.data?.rows_removed || 1) === 1 ? '' : 's'}`);
      queryClient.invalidateQueries({ queryKey: ['inbuilt-scraping'] });
    },
    onError: (err, id) => toast.error(`Could not delete ${id}: ${serverError(err)}`),
    onSettled: () => setPendingId(null),
  });

  const legacy = legacyUrl('/orderManagement/accountSettings');
  const header = (actions) => (
    <PageHeader
      icon={ScanSearch}
      title="Inbuilt scraping"
      description="The server scrapes usernames (an account's followers, a post's likers, a hashtag's or a location's users) and adds them, a chunk at a time, to the target list of the slots you name."
      actions={actions}
    />
  );

  if (q.isError && routeMissing(q.error)) {
    return (
      <div data-testid="inbuilt-scraping-page">
        {header(null)}
        <div className="surface flex items-start gap-3 p-4" data-testid="scrape-route-missing">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
          <p className="text-sm text-muted-foreground">
            This server does not have the inbuilt-scraping API yet (it comes with server r16o3). The legacy page still works:{' '}
            <a className="text-foreground underline break-all" href={legacy} target="_blank" rel="noreferrer">{legacy}</a>
          </p>
        </div>
      </div>
    );
  }

  if (q.isError) {
    return (
      <div data-testid="inbuilt-scraping-page">
        {header(null)}
        <div className="surface flex items-center gap-2 p-4 text-danger" data-testid="scrape-error">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span className="text-sm">Could not load inbuilt scraping. {serverError(q.error)}</span>
        </div>
      </div>
    );
  }

  if (q.isLoading || !form) {
    return (
      <div data-testid="inbuilt-scraping-page">
        {header(null)}
        <div className="space-y-3" data-testid="scrape-loading">{[0, 1, 2].map((i) => <div key={i} className="h-28 animate-pulse rounded-xl bg-secondary/60" />)}</div>
      </div>
    );
  }

  const d = q.data;
  const issues = saveIssues || d.grid_issues || [];
  const counts = issueCounts(issues);
  const lines = gridLines(form.grid).length;
  const readOnly = new Set(d.read_only || []);
  const active = Array.isArray(d.active) ? d.active : [];
  const completed = Array.isArray(d.completed) ? d.completed : [];

  return (
    <div data-testid="inbuilt-scraping-page">
      {header(
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => q.refetch()} disabled={q.isFetching} title="Refresh" aria-label="Refresh">
            <RefreshCw className={cn('h-4 w-4', q.isFetching && 'animate-spin')} />
          </Button>
          <Button onClick={() => saveMut.mutate()} disabled={!dirty || saveMut.isPending} data-testid="scrape-save"
            title={dirty ? 'Save the switch and the new rows' : 'Nothing changed'}>
            {saveMut.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Save
          </Button>
        </div>
      )}

      <div className="space-y-4">
        <Card className="surface">
          <CardHeader className="pb-3"><CardTitle className="text-base">Service</CardTitle></CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <Label htmlFor="inbuilt-on" className="text-sm font-medium">Inbuilt scraping service</Label>
                <p className="mt-0.5 text-xs text-muted-foreground" data-testid="scrape-service-words">
                  {form.enable
                    ? `ON - the server works through the rows below every ${d.cron_seconds || 10} seconds.`
                    : 'OFF - nothing is scraped or added to slots, and new rows wait here.'}
                </p>
              </div>
              <Switch id="inbuilt-on" checked={form.enable} onCheckedChange={(v) => setForm((f) => ({ ...f, enable: v }))} data-testid="scrape-toggle" />
            </div>
            <div className="flex items-start justify-between gap-4 rounded-lg bg-secondary/40 px-3 py-2.5" data-testid="scrape-credits">
              <div className="min-w-0">
                <p className="flex items-center gap-1.5 text-sm font-medium">
                  Target-scraping credits {readOnly.has('scraping_credits') && <Lock className="h-3 w-3 text-muted-foreground" aria-hidden />}
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">Each scraper request costs one. Set by your provider.</p>
              </div>
              <span className="text-2xl font-semibold tnum">{d.scraping_credits === null || d.scraping_credits === undefined ? '—' : Number(d.scraping_credits).toLocaleString()}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="surface">
          <CardHeader className="pb-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle className="text-base">New scraping rows</CardTitle>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground tnum">{lines} line{lines === 1 ? '' : 's'}</span>
                {counts.errors === 0 && counts.warnings === 0 && lines > 0 && !dirty && (
                  <Badge variant="outline" className="border-success/40 text-success"><CheckCircle2 className="mr-1 h-3 w-3" />valid</Badge>
                )}
                {counts.errors > 0 && <Badge variant="outline" className="border-danger/40 text-danger" data-testid="scrape-grid-errors">{counts.errors} error{counts.errors > 1 ? 's' : ''}</Badge>}
                {counts.warnings > 0 && <Badge variant="outline" className="border-warning/40 text-warning">{counts.warnings} warning{counts.warnings > 1 ? 's' : ''}</Badge>}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Textarea
              value={form.grid}
              onChange={(e) => setForm((f) => ({ ...f, grid: e.target.value }))}
              spellCheck={false}
              rows={6}
              className={cn('font-mono text-xs leading-relaxed', counts.errors > 0 && 'border-danger/50 focus-visible:ring-danger/30')}
              placeholder={EXAMPLE}
              aria-label="New scraping rows, one per line"
              data-testid="scrape-grid"
            />
            <p className="mt-2 text-xs text-muted-foreground">
              One scraping per line, nine fields separated by <code className="text-foreground">&lt;&gt;</code>:{' '}
              <code className="text-foreground break-words">{(d.grid_fields || Object.keys(FIELD_LABEL)).map((k) => FIELD_LABEL[k] || k).join(' <> ')}</code>
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Example: <code className="text-foreground break-all">{EXAMPLE}</code>. Types: {(d.known_types || []).join(', ')}.
              Direct assign = true re-uses usernames already scraped for that target, for other slots.
            </p>
            <p className="mt-1 text-xs text-muted-foreground" data-testid="scrape-slot-help">
              Slots{d.slot_count ? ` go from slot1 to slot${d.slot_count}` : ''}: slotN is the N-th slot in the order the server keeps
              this account's slots, which is usually, but not always, the slot named slotN.
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              The server takes these lines into Active scrapings within about {d.cron_seconds || 10} seconds while the service is on, and empties this box.
            </p>
            <IssueList issues={issues} />
          </CardContent>
        </Card>

        <Card className="surface">
          <CardHeader className="pb-3"><CardTitle className="text-base">Active scrapings <span className="ml-1 text-sm font-normal text-muted-foreground tnum">{active.length}</span></CardTitle></CardHeader>
          <CardContent>
            <RowsTable rows={active} kind="active" pendingId={pendingId} onDelete={(id) => delMut.mutate(id)} testId="scrape-active" />
            <NotStartedNote rows={active} />
          </CardContent>
        </Card>

        <Card className="surface">
          <CardHeader className="pb-3"><CardTitle className="text-base">History <span className="ml-1 text-sm font-normal text-muted-foreground">last 200 completed · {completed.length}</span></CardTitle></CardHeader>
          <CardContent>
            <RowsTable rows={completed} kind="completed" testId="scrape-completed" />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
